import { Toaster } from "sonner";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { registerServiceWorker } from "@/lib/push";
import { isNativeApp } from "@/lib/platform";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import Charter from "@/pages/Charter";
import Register from "@/pages/Register";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ChooseRole from "@/pages/ChooseRole";

import ProHome from "@/pages/pro/ProHome";
import ProActivate from "@/pages/pro/ProActivate";
import ProActivity from "@/pages/pro/ProActivity";
import ProMessages from "@/pages/pro/ProMessages";
import ProProfile from "@/pages/pro/ProProfile";
import ProAI from "@/pages/pro/ProAI";
import SubscriptionChoice from "@/pages/pro/SubscriptionChoice";
import MollieSimulate from "@/pages/pro/MollieSimulate";
import LeadDetail from "@/pages/pro/LeadDetail";

// Legal / SEO pages (public, no auth required)
import Privacy from "@/pages/legal/Privacy";
import DataDeletion from "@/pages/legal/DataDeletion";

import PartHome from "@/pages/particulier/PartHome";
import PartCreate from "@/pages/particulier/PartCreate";
import PartRequests from "@/pages/particulier/PartRequests";
import PartMessages from "@/pages/particulier/PartMessages";
import PartAI from "@/pages/particulier/PartAI";
import PartProfile from "@/pages/particulier/PartProfile";

import ThreadView from "@/pages/ThreadView";

import "./App.css";

function Loader() {
  return (
    <div className="app-shell flex items-center justify-center">
      <div className="h-10 w-10 rounded-full border-4 border-brand/20 border-t-brand animate-spin" />
    </div>
  );
}

// Fires a Google Analytics (gtag) page_view event on every route change.
function GtagPageviews() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const path = location.pathname + location.search;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
  return null;
}

function Protected({ role, children, allowUnactivated }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <Loader />;
  if (user === null) return <Navigate to="/onboarding" state={{ from: loc }} replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "pro" ? "/pro/home" : "/p/home"} replace />;
  }
  // Pro must pick a plan (tokens or premium) before accessing the app.
  // Premium requires an active subscription. Tokens choice grants immediate access.
  if (user.role === "pro" && !allowUnactivated) {
    const hasChosen = user.onboarding_choice === "tokens" || user.subscription_active;
    if (!hasChosen) return <Navigate to="/pro/choose-plan" replace />;
  }
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  const loc = useLocation();
  // Google OAuth callback lands with #session_id=...
  if ((loc.hash || window.location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }
  if (user === undefined) return <Loader />;
  if (user === null) return <Landing />;   // ← landing publique SEO-first
  if (user.role_pending) return <Navigate to="/choose-role" replace />;
  if (user.role === "pro") return <Navigate to="/pro/home" replace />;
  return <Navigate to="/p/home" replace />;
}

export default function App() {
  useEffect(() => { registerServiceWorker().catch(() => {}); }, []);
  useEffect(() => {
    // Toggle a class on <html> so global CSS can adapt (edge-to-edge, safe-areas, bigger tap targets)
    if (isNativeApp()) document.documentElement.classList.add("native-app");
    else document.documentElement.classList.remove("native-app");
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <GtagPageviews />
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/charter" element={<Charter />} />
          <Route path="/register/:role" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/choose-role" element={<Protected allowUnactivated><ChooseRole /></Protected>} />

          {/* Legal / SEO (public, aucune authentification requise) */}
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/data-deletion" element={<DataDeletion />} />

          {/* Pro */}
          <Route path="/pro/choose-plan" element={<Protected role="pro" allowUnactivated><SubscriptionChoice /></Protected>} />
          <Route path="/pro/mollie-simulate" element={<Protected role="pro" allowUnactivated><MollieSimulate /></Protected>} />
          <Route path="/pro/activate" element={<Protected role="pro" allowUnactivated><ProActivate /></Protected>} />
          <Route path="/pro/home" element={<Protected role="pro"><ProHome /></Protected>} />
          <Route path="/pro/lead/:id" element={<Protected role="pro"><LeadDetail /></Protected>} />
          <Route path="/pro/activity" element={<Protected role="pro"><ProActivity /></Protected>} />
          <Route path="/pro/messages" element={<Protected role="pro"><ProMessages /></Protected>} />
          <Route path="/pro/profile" element={<Protected role="pro"><ProProfile /></Protected>} />
          <Route path="/pro/ai" element={<Protected role="pro"><ProAI /></Protected>} />

          {/* Particulier */}
          <Route path="/p/home" element={<Protected role="particulier"><PartHome /></Protected>} />
          <Route path="/p/create" element={<Protected role="particulier"><PartCreate /></Protected>} />
          <Route path="/p/requests" element={<Protected role="particulier"><PartRequests /></Protected>} />
          <Route path="/p/messages" element={<Protected role="particulier"><PartMessages /></Protected>} />
          <Route path="/p/ai" element={<Protected role="particulier"><PartAI /></Protected>} />
          <Route path="/p/profile" element={<Protected role="particulier"><PartProfile /></Protected>} />

          {/* Shared */}
          <Route path="/thread/:leadId/:partnerId" element={<Protected><ThreadView /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
